import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { EncryptionService } from './encryption.service';

/**
 * Secure Storage Service
 * Wraps localStorage with encryption for sensitive data
 */
@Injectable({
  providedIn: 'root',
})
export class SecureStorageService {
  // Keys that should be encrypted (sensitive data)
  private readonly SENSITIVE_KEYS = [
    'authToken',
    'id',
    'user',
    'permissions',
    'email',
    'userDetails',
  ];

  private isBrowser: boolean;

  constructor(
    private encryptionService: EncryptionService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  /**
   * Store data in localStorage with encryption for sensitive keys
   */
  setItem(key: string, value: string): void {
    if (!key || !this.isBrowser) return;

    try {
      if (this.isSensitiveKey(key)) {
        const encryptedValue = this.encryptionService.encrypt(value);
        localStorage.setItem(key, encryptedValue);
      } else {
        localStorage.setItem(key, value);
      }
    } catch (error) {
      console.error(`SecureStorage setItem error for key "${key}":`, error);
    }
  }

  /**
   * Retrieve data from localStorage with automatic decryption for sensitive keys
   */
  getItem(key: string): string | null {
    if (!key || !this.isBrowser) return null;

    try {
      const value = localStorage.getItem(key);

      if (!value) return null;

      if (this.isSensitiveKey(key)) {
        // Check if value is encrypted
        if (this.encryptionService.isEncrypted(value)) {
          return this.encryptionService.decrypt(value);
        }
        // If not encrypted (legacy data), return as-is and re-encrypt it
        this.setItem(key, value);
        return value;
      }

      return value;
    } catch (error) {
      console.error(`SecureStorage getItem error for key "${key}":`, error);
      return null;
    }
  }

  /**
   * Remove item from localStorage
   */
  removeItem(key: string): void {
    if (!key || !this.isBrowser) return;
    localStorage.removeItem(key);
  }

  /**
   * Clear all localStorage data
   */
  clear(): void {
    if (!this.isBrowser) return;
    localStorage.clear();
  }

  /**
   * Store object as JSON with encryption
   */
  setObject(key: string, value: object): void {
    if (!key || value === null || value === undefined || !this.isBrowser) return;

    try {
      const jsonString = JSON.stringify(value);
      this.setItem(key, jsonString);
    } catch (error) {
      console.error(`SecureStorage setObject error for key "${key}":`, error);
    }
  }

  /**
   * Retrieve and parse JSON object with decryption
   */
  getObject<T>(key: string): T | null {
    if (!this.isBrowser) return null;

    try {
      const value = this.getItem(key);
      if (!value) return null;
      return JSON.parse(value) as T;
    } catch (error) {
      console.error(`SecureStorage getObject error for key "${key}":`, error);
      return null;
    }
  }

  /**
   * Check if a key exists in localStorage
   */
  hasKey(key: string): boolean {
    if (!this.isBrowser) return false;
    return localStorage.getItem(key) !== null;
  }

  /**
   * Check if key contains sensitive data that needs encryption
   */
  private isSensitiveKey(key: string): boolean {
    return this.SENSITIVE_KEYS.includes(key);
  }

  /**
   * Migrate existing unencrypted data to encrypted format
   * Call this method once during app initialization
   */
  migrateToEncrypted(): void {
    if (!this.isBrowser) return;

    this.SENSITIVE_KEYS.forEach((key) => {
      const value = localStorage.getItem(key);
      if (value && !this.encryptionService.isEncrypted(value)) {
        // Re-save to encrypt
        const encryptedValue = this.encryptionService.encrypt(value);
        localStorage.setItem(key, encryptedValue);
      }
    });
  }
}
